import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Import Firebase configuration (assumed to be initialized in a separate file)
import { database, auth } from '../firebase';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Expense Report Display Component
const ExpenseReportDisplay = ({ formData, filters, branchProfile, expenseData }) => {
  const parseExpenseDate = (timestamp) => {
    if (!timestamp) return null;
    return new Date(timestamp);
  };

  const formatDisplayDate = (timestamp) => {
    const date = parseExpenseDate(timestamp);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  let filteredExpenseData = expenseData;

  // Apply date filter
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredExpenseData = filteredExpenseData.filter(item => {
      const itemDate = parseExpenseDate(item.createdAt);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  // Apply expense type filter
  if (!filters.expenseType.includes("All Expense Types")) {
    filteredExpenseData = filteredExpenseData.filter(item => 
      filters.expenseType.includes(item.type)
    );
  }

  // Apply payment method filter
  if (!filters.method.includes("All Payment Methods")) {
    filteredExpenseData = filteredExpenseData.filter(item => 
      filters.method.includes(item.method)
    );
  }

  let displayData = [];

  // Process data based on search criteria and report type
  if ((formData.searchBy === "datewise" || formData.searchBy === "typewise" || formData.searchBy === "methodwise") && formData.reportType === "abstract") {
    const groupedData = filteredExpenseData.reduce((acc, item) => {
      const key = formData.searchBy === "datewise" ? formatDisplayDate(item.createdAt) : 
                  formData.searchBy === "typewise" ? item.type : 
                  item.method;
      if (!acc[key]) {
        acc[key] = {
          key: key,
          amount: 0,
          gst: 0,
          total: 0,
          count: 0
        };
      }
      const amount = parseFloat(item.amount) || 0;
      const gstAmount = amount * ((parseFloat(item.gst) || 0) / 100);
      acc[key].amount += amount;
      acc[key].gst += gstAmount;
      acc[key].total += amount + gstAmount;
      acc[key].count += 1;
      return acc;
    }, {});
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => {
      if (formData.searchBy === "datewise") {
        const dateA = a.key.split('/').reverse().join('-');
        const dateB = b.key.split('/').reverse().join('-');
        return new Date(dateA) - new Date(dateB);
      }
      return a.key.localeCompare(b.key);
    });
  } else {
    // For detail reports
    displayData = [...filteredExpenseData];
    displayData.sort((a, b) => parseExpenseDate(a.createdAt) - parseExpenseDate(b.createdAt));
  }

  displayData = Array.isArray(displayData) ? displayData : [];

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  // Calculate totals
  const totalAmount = displayData.reduce((sum, item) => {
    if (formData.reportType === "abstract") {
      return sum + (parseFloat(item.amount) || 0);
    } else {
      return sum + (parseFloat(item.amount) || 0);
    }
  }, 0);
  
  const totalGstAmount = displayData.reduce((sum, item) => {
    if (formData.reportType === "abstract") {
      return sum + (parseFloat(item.gst) || 0);
    } else {
      const amount = parseFloat(item.amount) || 0;
      const gstPercent = parseFloat(item.gst) || 0;
      return sum + (amount * (gstPercent / 100));
    }
  }, 0);
  
  const totalExpense = totalAmount + totalGstAmount;

  const renderTableHeaders = () => {
    if ((formData.searchBy === "datewise" || formData.searchBy === "typewise" || formData.searchBy === "methodwise") && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            {formData.searchBy === "datewise" ? "Date" : formData.searchBy === "typewise" ? "Expense Type" : "Payment Method"}
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Count</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total</th>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Expense Type</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST %</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Payment Method</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total</th>
        </tr>
      );
    }
  };

  const renderTableRows = () => {
    if (displayData.length === 0) {
      const colSpan = (formData.searchBy === "datewise" || formData.searchBy === "typewise" || formData.searchBy === "methodwise") && formData.reportType === "abstract" ? 6 : 8;
      return (
        <tr>
          <td colSpan={colSpan} style={{ 
            border: '1px solid #cbd5e1', 
            padding: '30px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No expense data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => {
      if ((formData.searchBy === "datewise" || formData.searchBy === "typewise" || formData.searchBy === "methodwise") && formData.reportType === "abstract") {
        return (
          <tr key={item.key} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.key}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.count}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.amount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.gst) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.total) || 0).toFixed(2)}</td>
          </tr>
        );
      } else {
        const amount = parseFloat(item.amount) || 0;
        const gstPercent = parseFloat(item.gst) || 0;
        const gstAmount = amount * (gstPercent / 100);
        const itemTotal = amount + gstAmount;
        return (
          <tr key={item.id || index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.createdAt)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.type}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{amount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{gstPercent.toFixed(2)}%</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{gstAmount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.method}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{itemTotal.toFixed(2)}</td>
          </tr>
        );
      }
    });
  };

  const renderTotalRow = () => {
    if (displayData.length === 0) return null;

    if ((formData.searchBy === "datewise" || formData.searchBy === "typewise" || formData.searchBy === "methodwise") && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalExpense.toFixed(2)}</td>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalExpense.toFixed(2)}</td>
        </tr>
      );
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {formData.searchBy.charAt(0).toUpperCase() + formData.searchBy.slice(1)} Expense Report
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {new Date(formData.fromDate).toLocaleDateString('en-GB')}</span>
              <span><strong>To:</strong> {new Date(formData.toDate).toLocaleDateString('en-GB')}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '11px', 
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
          </tbody>
        </table>
      </div>

      {displayData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total GST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalGstAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Grand Total</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalExpense.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Expense Report Generator Component
const ExpenseReportGenerator = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("datewise");
  const [reportType, setReportType] = useState("detail");
  const [filters, setFilters] = useState({
    expenseType: ["All Expense Types"],
    method: ["All Payment Methods"]
  });
  const [expenseTypes, setExpenseTypes] = useState(["All Expense Types"]);
  const [paymentMethods] = useState(["All Payment Methods", "cash", "online"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
   
  });
  const [expenseData, setExpenseData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  const getAvailableReportTypes = () => {
    switch (searchBy) {
      case "datewise":
      case "typewise":
      case "methodwise":
        return [
          { value: "detail", label: "Detail" },
          { value: "abstract", label: "Abstract" }
        ];
      default:
        return [
          { value: "detail", label: "Detail" },
          { value: "abstract", label: "Abstract" }
        ];
    }
  };

  useEffect(() => {
    const availableTypes = getAvailableReportTypes();
    if (!availableTypes.some(type => type.value === reportType)) {
      setReportType(availableTypes[0].value);
    }
  }, [searchBy, reportType]);

 

// Update useEffect to fetch branch profile data
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    setCurrentUser(user);
    setLoading(false);
    if (!user) {
      setExpenseData([]);
      setExpenseTypes(["All Expense Types"]);
      setError('Please log in to view expense reports.');
    } else {
      // Fetch branch profile data
      const encodedEmail = encodeEmailForPath(user.email);
      const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
      onValue(branchProfileRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const profiles = Object.values(data);
          const latestProfile = profiles[profiles.length - 1];
          setBranchProfile({
            branchName: latestProfile.branchName || "SUPREME TRADERS",
            address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
            phone: latestProfile.mobileNo || '',
            city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
          });
        }
      }, (error) => {
        console.error('Error fetching branch profile: ', error);
        setError('Failed to load branch profile. Please try again.');
      });
    }
  });

  return () => {
    unsubscribe();
    if (currentUser) {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
      off(branchProfileRef);
    }
  };
}, []);

// Update the existing expenses useEffect to depend on currentUser
useEffect(() => {
  if (!currentUser) {
    setLoading(false);
    return;
  }

  const encodedEmail = encodeEmailForPath(currentUser.email);
  const expensesRef = ref(database, `users/${encodedEmail}/expenses`);

  const fetchExpenses = () => {
    onValue(expensesRef, (snapshot) => {
      const data = snapshot.val();
      const expenseList = data
        ? Object.entries(data).map(([id, value]) => ({ id, ...value }))
        : [];
      setExpenseData(expenseList);
      const types = ["All Expense Types", ...new Set(expenseList.map(e => e.type))];
      setExpenseTypes(types);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching expenses: ', error);
      setError('Failed to load expenses. Please try again.');
      setLoading(false);
    });
  };

  fetchExpenses();

  return () => {
    off(expensesRef);
  };
}, [currentUser]);
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    if (!currentUser) {
      setError('You must be logged in to generate reports.');
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      setError('From date cannot be later than To date.');
      return;
    }
    setError(null);
    setShowReport(true);
  };

  const handlePrint = () => {
    if (!currentUser) {
      setError('You must be logged in to print reports.');
      return;
    }
    const printContent = (
      <ExpenseReportDisplay 
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        expenseData={expenseData}
      />
    );

    const htmlString = ReactDOMServer.renderToString(printContent);

    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Daily Expense Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              padding: 4px !important;
              font-size: 12px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .print-only {
              width: 93% !important;
              max-width: none !important;
              overflow: visible !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Expense Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '4rem 2rem', 
        color: '#718096', 
        fontSize: '1.1rem',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>🔐</div>
        <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>Expense Report</h3>
        <p>Please log in to generate expense reports.</p>
      </div>
    );
  }

  const availableReportTypes = getAvailableReportTypes();

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: '0';
              top: '0';
              width: '100%';
              margin: '0';
              padding: '20px';
              box-shadow: 'none';
              border: 'none';
            }
          }
        `}
      </style>
      
      {error && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          borderRadius: '0.75rem',
          fontWeight: '500',
          fontSize: '1rem',
          backgroundColor: '#fef2f2',
          color: '#b91c1c',
          border: '2px solid #fecaca',
          textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Daily Expense Report</h1>
      </div>
      
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value=''>Select</option>
            <option value="datewise">Datewise</option>
          
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            {availableReportTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Checkboxes
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
      }}>
        <FilterSection 
          title="Expense Type" 
          options={expenseTypes} 
          selected={filters.expenseType} 
          onChange={(value) => handleFilterChange("expenseType", value)}
        />
        <FilterSection 
          title="Payment Method" 
          options={paymentMethods} 
          selected={filters.method} 
          onChange={(value) => handleFilterChange("method", value)}
        />
      </div> */}

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Expense Report Display */}
      {showReport && (
        <ExpenseReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          expenseData={expenseData}
        />
      )}
    </div>
  );
};

export default ExpenseReportGenerator;